// KbTrainingShell.jsx — branded wrapper for the KB-driven Training UI.
//
// Every visible chrome element (breadcrumb, title, accent colour) is
// pulled from the tenant's own branding — no "Total Fusion" strings
// appear here, even in class names. For Yabbit tenants we auto-apply
// a Yabbit palette (teal accent + dark navy heading) sourced from the
// public brand guidelines on yabbit.com.au.
import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, GraduationCap } from 'lucide-react';
import '../tfvf/tfvf.css';

// Yabbit brand palette — matches yabbit.com.au
const YABBIT = {
  accent: '#22a196',     // teal
  accentDeep: '#0d4d5a', // dark navy-teal (title colour)
  surface: '#f2fbfa',    // pale mint surface
};

const KbTrainingShell = ({ branding, children }) => {
  const company = branding?.company_name || 'Your Company';
  const isYabbit = !!branding?.is_yabbit_tenant;
  const accent = branding?.custom_color
    || (isYabbit ? YABBIT.accent : '');

  const titleColor = isYabbit
    ? YABBIT.accentDeep
    : (accent || 'var(--tf-forest)');

  const chipBg = accent || (isYabbit ? YABBIT.accent : 'var(--tf-forest)');
  const cardSurface = isYabbit
    ? { background: `linear-gradient(135deg, ${YABBIT.surface} 0%, #ffffff 60%)`,
        borderColor: 'rgba(34,161,150,0.18)' }
    : undefined;

  return (
    <div className="tf-scope space-y-6" data-testid="kb-training-shell" data-yabbit={isYabbit ? '1' : '0'}>
      <div
        className="tf-card rounded-xl p-5 flex items-center gap-4"
        style={cardSurface}
      >
        <div
          className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: chipBg }}
        >
          {branding?.custom_logo_url ? (
            <img
              src={branding.custom_logo_url}
              alt={company}
              className="w-8 h-8 object-contain"
            />
          ) : (
            <GraduationCap className="w-6 h-6 text-white" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-xs uppercase tracking-wide flex items-center gap-1"
            style={{ color: isYabbit ? 'rgba(13,77,90,0.6)' : 'var(--tf-muted)' }}
            data-testid="kb-training-breadcrumb"
          >
            <Link to="/">VoiceFabric</Link>
            <ChevronRight className="w-3 h-3 opacity-70" />
            <span className="font-semibold" style={{ color: titleColor }}>{company}</span>
            <ChevronRight className="w-3 h-3 opacity-70" />
            Training &amp; Retraining
          </p>
          <h1
            className="text-2xl font-black tracking-tight"
            style={{ color: titleColor }}
            data-testid="kb-training-title"
          >
            {isYabbit ? `${company} · VoiceFabric Training` : `${company} Training`}
          </h1>
          <p className="text-sm" style={{ color: isYabbit ? 'rgba(13,77,90,0.7)' : 'var(--tf-muted)' }}>
            {isYabbit
              ? 'Yabbit VoiceFabric training — the same 5 role KBs that grade your calls, so every skill you rehearse counts on the scorecard.'
              : "Built directly from your team's knowledge bases — so what you learn is exactly what your calls are graded on."}
          </p>
        </div>
      </div>
      {children}
    </div>
  );
};

export default KbTrainingShell;
